import { useMemo, useState } from 'react';
import { TagIcon, XIcon } from 'lucide-react';

import {
  Combobox,
  ComboboxContent,
  ComboboxInput,
  ComboboxInputGroup,
  ComboboxItem,
  ComboboxList,
  ComboboxTrigger,
} from '@/components/ui/combobox';
import { matchLabelsByName } from '../labelName';
import { RemovableLabelChip } from './RemovableLabelChip';
import {
  FOCUS_RING,
  HEADER_PILL,
  HEADER_PILL_ACTIVE,
  HEADER_PILL_IDLE,
} from '@/shared/components/pillClasses';
import type { LabelView } from '@/lib/api/generated/model';

import type { LabelNarrowing } from '../labelFilter';

/** The option identifier standing for "carries no labels" inside the list. */
const UNLABELLED_OPTION = '__unlabelled__';

export interface LabelFilterControlProps {
  /** The workspace's vocabulary — every label, not only the ones in use. */
  registry: readonly LabelView[];
  narrowing: LabelNarrowing;
  onChange: (next: LabelNarrowing) => void;
  /** The trigger's visible word; "Filter" unless the caller says otherwise. */
  trigger?: string;
}

/**
 * Narrow a board — or the Overview — to the tasks carrying chosen labels.
 *
 * Reads as a further member of the board's existing pill family rather than as
 * a new kind of control: a trigger that looks like the three state pills, then
 * one removable pill per active label. What keeps the two vocabularies apart is
 * the **dot**: the state pills carry a leading one, and label pills carry none.
 * In a row that mixes them that is the only discriminator, which is why it is
 * asserted in the tests rather than left to styling.
 *
 * The vocabulary lives behind the trigger instead of being spelled out along
 * the row, because a workspace's label list grows without bound and the three
 * state pills have to stay on the row whatever happens to it.
 */
export function LabelFilterControl({
  registry,
  narrowing,
  onChange,
  trigger = 'Filter',
}: LabelFilterControlProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  // The board filter can be typed into, exactly like the task picker. Without
  // this the control was a bare list: fine at one label, a scroll-and-hunt at
  // thirty, which is the point at which a filter stops being faster than reading
  // the board.
  const matches = useMemo(
    () => matchLabelsByName(registry, query),
    [registry, query],
  );

  const selectedIds = narrowing?.kind === 'labels' ? narrowing.ids : [];
  const isUnlabelled = narrowing?.kind === 'unlabelled';

  // Only labels the vocabulary still holds get a pill. A deep link can name one
  // that has since been deleted; the narrowing keeps it — that is what makes
  // the board honestly empty — but there is no name to put on a pill, and
  // inventing one would be worse than showing nothing.
  const activeLabels = selectedIds.flatMap((id) => {
    const label = registry.find((candidate) => candidate.id === id);
    return label ? [label] : [];
  });

  /**
   * Turn the combobox's new selection into a narrowing.
   *
   * The two variants are mutually exclusive (FR-018), and that is enforced here,
   * once, rather than by every consumer guarding against a narrowing that can
   * never match. Whichever of the two the reader just added wins: picking
   * Unlabelled drops the named labels, and picking a name drops Unlabelled.
   */
  function apply(next: readonly string[]) {
    const wantsUnlabelled = next.includes(UNLABELLED_OPTION);
    const names = next.filter((id) => id !== UNLABELLED_OPTION);

    if (wantsUnlabelled && !isUnlabelled) {
      onChange({ kind: 'unlabelled' });
      return;
    }
    // An unfiltered view says so by carrying nothing. `{ ids: [] }` would put a
    // bare `labels=` in the address and read as a filter that is on.
    onChange(names.length > 0 ? { kind: 'labels', ids: names } : undefined);
  }

  function remove(id: string) {
    const next = selectedIds.filter((candidate) => candidate !== id);
    onChange(next.length > 0 ? { kind: 'labels', ids: next } : undefined);
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      {/*
        The trigger comes first, and stays first.

        It used to follow the active chips, so every label added pushed it
        further right — the control you press to add a *second* filter had
        moved by the time you looked for it, and kept moving. A control that
        changes position as a result of being used is the one thing a filter
        row must not do. Anchored here, the chips grow away from it.
      */}
      <Combobox<string, true>
        multiple
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          // A query left standing means reopening the list shows it still
          // filtered by a word the reader typed a minute ago and cannot see —
          // the list looks short for no stated reason.
          if (!next) setQuery('');
        }}
        /*
          Genuinely multiple, and genuinely valued.

          Held as a single-select with `value={null}` this reported no selection
          at all: `aria-selected` stayed false on every option and the check
          indicator never drew, so reopening the list said nothing about which
          labels were already in force — and a screen reader was told nothing
          whatsoever, even though clicking an option is a toggle.

          The value is derived from `narrowing`, which stays the one source of
          truth; the exclusivity rule below is what a plain multi-select cannot
          express on its own.
        */
        value={isUnlabelled ? [UNLABELLED_OPTION] : [...selectedIds]}
        onValueChange={(next) => {
          apply(next);
        }}
        items={[UNLABELLED_OPTION, ...registry.map((label) => label.id)]}
        itemToStringLabel={(id) =>
          id === UNLABELLED_OPTION
            ? 'Unlabelled'
            : (registry.find((label) => label.id === id)?.name ?? id)
        }
      >
        <ComboboxTrigger
          // Named explicitly: the trigger is exposed as a combobox, and that
          // role takes no accessible name from its own text, so the visible
          // word alone would leave it announced as unnamed.
          //
          // The visible word is the *verb*. It read "Label" — a noun, beside a
          // tag icon, on a board header — which lands at least as plausibly as
          // "add a label to something" as it does "narrow to a label": an action
          // on the board rather than a lens over it. A screen reader was already
          // told "Filter by label" while a sighted reader got the ambiguous
          // version, which is the wrong way round.
          aria-label="Filter by label"
          className={`${HEADER_PILL} ${HEADER_PILL_IDLE}`}
        >
          <TagIcon aria-hidden="true" className="size-3" />
          {trigger}
        </ComboboxTrigger>

        {/*
          Sized to its own contents, not to the trigger.

          `ComboboxContent` defaults to `w-(--anchor-width)`, which is right for
          the task picker — its anchor is a full-width field. Here the anchor is
          the small "Label" pill, so inheriting that width pinned the popup to
          roughly ninety pixels: the search field and the option names were
          clipped mid-word and the list grew a horizontal scrollbar. The max
          width still comes from the viewport, so this cannot run off-screen.
        */}
        <ComboboxContent className="w-auto min-w-56">
          <ComboboxInputGroup className="mb-1">
            <ComboboxInput
              aria-label="Find a label"
              placeholder="Find a label…"
              value={query}
              onChange={(event) => {
                setQuery(event.target.value);
              }}
            />
          </ComboboxInputGroup>
          <ComboboxList>
            {/*
              First, and always offered: "show me what nobody has said anything
              about" is the question that finds the tasks a vocabulary has
              missed, and it would be unreachable if it sorted among the names.
            */}
            <ComboboxItem value={UNLABELLED_OPTION}>Unlabelled</ComboboxItem>
            {matches.map((label) => (
              <ComboboxItem key={label.id} value={label.id}>
                {label.name}
              </ComboboxItem>
            ))}
          </ComboboxList>
        </ComboboxContent>
      </Combobox>

      {isUnlabelled ? (
        <span
          data-testid="label-filter-pill-none"
          className={`${HEADER_PILL} ${HEADER_PILL_ACTIVE}`}
        >
          Unlabelled
          <button
            type="button"
            aria-label="Remove the unlabelled filter"
            onClick={() => {
              onChange(undefined);
            }}
            className={`${FOCUS_RING} -mr-1 rounded-full p-0.5 opacity-70 transition-opacity hover:opacity-100`}
          >
            <XIcon aria-hidden="true" className="size-3" />
          </button>
        </span>
      ) : null}

      {/*
        An active label filter is the label itself, in its own colour, with a way
        to drop it — not a differently-styled pill saying the same word.

        It used to render as an inverted dark pill, so `acme` on a card and `acme`
        in this row were two different-looking objects on one screen. Now that
        slots carry colour that would be worse than inconsistent: the inverted
        fill would suppress the very hue the card uses to identify the label.

        `Unlabelled` above keeps the pill form deliberately — it names an absence,
        so there is no label whose colour it could wear.
      */}
      {activeLabels.map((label) => (
        <span key={label.id} data-testid={`label-filter-pill-${label.id}`}>
          {/*
            No `onNarrow`: every chip here already *is* the narrowing, so a
            control that re-applied it would do nothing a reader could observe.
            Without it the whole pill removes — see `RemovableLabelChip`, where
            the absence of narrowing is what chooses the one-target shape.

            `header` density, because this chip stands in a row of state pills
            and has to occupy the same box they do (FR-023).
          */}
          <RemovableLabelChip
            label={label}
            onRemove={remove}
            density="header"
          />
        </span>
      ))}
    </div>
  );
}
